import { useState, useCallback } from 'react';
import client from '../utils/fetchClient';

const CACHE_DURATION = {
  TICKETS: 5 * 60 * 1000,      // 5 minutes
  BASIC_ANALYTICS: 15 * 60 * 1000,  // 15 minutes
  AI_ANALYTICS: 2 * 60 * 60 * 1000,  // 2 hours for AI results
};

const cache = new Map();

const useAnalyticsAPI = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleAPIError = useCallback((error, endpoint) => {
    console.error(`API Error on ${endpoint}:`, error);

    // Handle specific error types
    if (error.message.includes('503')) {
      return {
        type: 'AI_UNAVAILABLE',
        message: 'AI analysis is temporarily unavailable. Showing basic analysis.',
        fallback: true
      };
    }

    if (error.message.includes('401')) {
      return {
        type: 'AUTH_ERROR',
        message: 'Invalid API key. Please check your credentials.',
        fallback: false
      };
    }

    if (error.message.includes('404')) {
      return {
        type: 'NOT_FOUND',
        message: 'Requested resource not found.',
        fallback: false
      };
    }

    // Network or other errors
    return {
      type: 'NETWORK_ERROR',
      message: 'Network error. Please try again.',
      fallback: true
    };
  }, []);

  // Check if cached AI result exists and is still valid
  const getCachedAIResult = useCallback(async (cacheKey) => {
    try {
      const response = await client.get('/api/bugs/analytics/cache', {
        query: { key: cacheKey }
      });
      
      if (response.ok) {
        const cached = await response.json();
        if (cached.data && Date.now() - new Date(cached.createdAt).getTime() < CACHE_DURATION.AI_ANALYTICS) {
          return cached.data;
        }
      }
    } catch (error) {
      // Cache miss or error - proceed with fresh request
      console.log('Cache miss or error:', error.message);
    }
    return null;
  }, []);

  // Save AI result to database cache
  const saveAIResultToCache = useCallback(async (cacheKey, data) => {
    try {
      await client.post('/api/bugs/analytics/cache', {
        body: {
          key: cacheKey,
          data: data,
          metadata: {
            endpoint: cacheKey.split('?')[0],
            generatedAt: new Date().toISOString(),
            ttl: CACHE_DURATION.AI_ANALYTICS
          }
        }
      });
    } catch (error) {
      console.warn('Failed to cache AI result:', error.message);
      // Don't throw - caching failure shouldn't break the main flow
    }
  }, []);

  const makeRequest = useCallback(async (endpoint, options = {}) => {
    // Build query string for cache key
    const queryParams = new URLSearchParams();
    if (options.query) {
      Object.entries(options.query).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, value);
        }
      });
    }
    const cacheKey = `${endpoint}?${queryParams.toString()}`;
    
    const cached = cache.get(cacheKey);
    
    // Check memory cache first for basic analytics (but not for paginated requests)
    if (cached && !endpoint.includes('ai') && !options.query?.page && Date.now() - cached.timestamp < CACHE_DURATION.BASIC_ANALYTICS) {
      return cached.data;
    }

    // For AI endpoints, check database cache
    if (endpoint.includes('ai-insights')) {
      const cachedAIResult = await getCachedAIResult(cacheKey);
      if (cachedAIResult) {
        console.log('Using cached AI result from database');
        return cachedAIResult;
      }
    }

    setLoading(true);
    setError('');

    try {
      let response;
      
      // Use POST for processing endpoints, GET for others
      if (endpoint.includes('process-all-tickets')) {
        response = await client.post(endpoint, options);
      } else {
        response = await client.get(endpoint, options);
      }
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      
      // Cache the result (but not paginated requests or POST requests)
      if (endpoint.includes('ai-insights') && !endpoint.includes('process-all-tickets')) {
        // Save AI results to database
        await saveAIResultToCache(cacheKey, data);
      } else if (!options.query?.page && !endpoint.includes('process-all-tickets')) {
        // Save basic analytics to memory cache (but not paginated or POST)
        cache.set(cacheKey, { data, timestamp: Date.now() });
      }
      
      return data;
    } catch (error) {
      const errorInfo = handleAPIError(error, endpoint);
      setError(errorInfo.message || error.message || 'An error occurred');
      throw error;
    } finally {
      setLoading(false);
    }
  }, [handleAPIError, getCachedAIResult, saveAIResultToCache]);

  const makeRequestWithFallback = useCallback(async (primaryEndpoint, fallbackEndpoint, options = {}) => {
    try {
      return await makeRequest(primaryEndpoint, options);
    } catch (error) {
      const errorInfo = handleAPIError(error, primaryEndpoint);
      
      if (errorInfo.fallback && fallbackEndpoint) {
        console.log(`Falling back to ${fallbackEndpoint}`);
        try {
          return await makeRequest(fallbackEndpoint, options);
        } catch (fallbackError) {
          console.error('Fallback also failed:', fallbackError);
          throw fallbackError;
        }
      }
      
      throw error;
    }
  }, [makeRequest, handleAPIError]);

  // Clear all cache (memory + database)
  const clearCache = useCallback(async (cacheType = 'all') => {
    if (cacheType === 'memory' || cacheType === 'all') {
      cache.clear();
    }
    
    if (cacheType === 'ai' || cacheType === 'all') {
      try {
        await client.delete('/api/bugs/analytics/cache');
        console.log('Database cache cleared');
      } catch (error) {
        console.warn('Failed to clear database cache:', error.message);
      }
    }
  }, []);

  // Invalidate specific cache entry
  const invalidateCache = useCallback(async (endpoint, options = {}) => {
    const cacheKey = `${endpoint}?${JSON.stringify(options.query || {})}`;
    
    // Remove from memory cache
    cache.delete(cacheKey);
    
    // Remove from database cache if it's an AI endpoint
    if (endpoint.includes('ai-insights')) {
      try {
        await client.delete('/api/bugs/analytics/cache', {
          query: { key: cacheKey }
        });
      } catch (error) {
        console.warn('Failed to invalidate database cache:', error.message);
      }
    }
  }, []);

  // Check cache status
  const getCacheInfo = useCallback(async () => {
    try {
      const response = await client.get('/api/bugs/analytics/cache/info');
      const dbCacheInfo = response.ok ? await response.json() : { count: 0, size: '0 MB' };
      
      return {
        memory: {
          count: cache.size,
          keys: Array.from(cache.keys())
        },
        database: dbCacheInfo
      };
    } catch (error) {
      return {
        memory: { count: cache.size, keys: Array.from(cache.keys()) },
        database: { count: 0, size: '0 MB', error: error.message }
      };
    }
  }, []);

  return { 
    makeRequest, 
    makeRequestWithFallback, 
    loading, 
    error, 
    clearCache,
    invalidateCache,
    getCacheInfo
  };
};

export default useAnalyticsAPI;
